import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, MessageSquare, CreditCard, LogOut, User, 
  Clock, CheckCircle2, AlertCircle, Send, Loader2,
  ChevronRight, Mail, Phone, MapPin, Package, DollarSign, Receipt,
  XCircle, ArrowUpDown, Filter, Settings, Save, Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, isAuthenticated, loading, updateUser } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [isLoadingQuotes, setIsLoadingQuotes] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [newMessage, setNewMessage] = useState({ subject: '', content: '' });
  
  // Payment state
  const [packages, setPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  
  // Quote filtering/sorting state
  const [quoteSortOrder, setQuoteSortOrder] = useState('desc');
  const [quoteStatusFilter, setQuoteStatusFilter] = useState('all');
  
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({ name: '', phone: '', address: '' });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  
  // Declining quote state
  const [isDecliningQuote, setIsDecliningQuote] = useState(null);
  
  // Cash payment state
  const [isPayingCash, setIsPayingCash] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchQuotes();
      fetchMessages();
      fetchPackages();
      fetchPayments();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        phone: user.phone || '',
        address: user.address || ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchQuotes();
    }
  }, [quoteSortOrder, quoteStatusFilter]);

  const fetchQuotes = async () => {
    try {
      const params = new URLSearchParams();
      params.append('sort', quoteSortOrder);
      if (quoteStatusFilter !== 'all') {
        params.append('status', quoteStatusFilter);
      }
      const response = await axios.get(`${API}/member/quotes?${params.toString()}`, {
        headers: getAuthHeader()
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setIsLoadingQuotes(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/member/messages`, {
        headers: getAuthHeader()
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setIsLoadingPackages(false);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/member/payments`, {
        headers: getAuthHeader()
      });
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  const handleQuotePayment = async (quoteId) => {
    setIsProcessingPayment(true);
    setSelectedPackage(quoteId);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(
        `${API}/payments/quote-checkout`,
        { quote_id: quoteId, origin_url: originUrl },
        { headers: getAuthHeader() }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment. Please try again.');
    } finally {
      setIsProcessingPayment(false);
      setSelectedPackage(null);
    }
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-amber-100 text-amber-700', label: 'Pending' },
      paid: { color: 'bg-green-100 text-green-700', label: 'Paid' },
      cancelled: { color: 'bg-red-100 text-red-700', label: 'Cancelled' },
      failed: { color: 'bg-red-100 text-red-700', label: 'Failed' },
      expired: { color: 'bg-slate-100 text-slate-700', label: 'Expired' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.subject || !newMessage.content) {
      toast.error('Please fill in both subject and message');
      return;
    }

    setIsSendingMessage(true);

    try {
      await axios.post(`${API}/member/messages`, newMessage, {
        headers: getAuthHeader()
      });
      toast.success('Message sent! Our team will respond soon.');
      setNewMessage({ subject: '', content: '' });
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Pending' },
      reviewed: { icon: CheckCircle2, color: 'bg-blue-100 text-blue-700', label: 'Reviewed' },
      'in-progress': { icon: Clock, color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
      completed: { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Completed' },
      complete: { icon: CheckCircle2, color: 'bg-green-100 text-green-700', label: 'Complete' },
      cancelled: { icon: AlertCircle, color: 'bg-red-100 text-red-700', label: 'Cancelled' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const handleDeclineQuote = async (quoteId) => {
    setIsDecliningQuote(quoteId);
    try {
      await axios.post(`${API}/member/quotes/${quoteId}/decline`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Quote declined. The admin will provide a revised quote.');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline quote');
    } finally {
      setIsDecliningQuote(null);
    }
  };

  const handlePayCash = async (quoteId) => {
    setIsPayingCash(quoteId);
    try {
      await axios.post(`${API}/member/quotes/${quoteId}/pay-cash`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Cash payment requested. The admin has been notified.');
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request cash payment');
    } finally {
      setIsPayingCash(null);
    }
  };

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const response = await axios.put(`${API}/member/profile/update`, profileData, {
        headers: getAuthHeader()
      });
      toast.success('Profile updated successfully');
      if (response.data.user && updateUser) {
        updateUser(response.data.user);
      }
      setIsEditingProfile(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setIsSavingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-navy" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-[80vh] bg-brand-surface py-8" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-brand-navy rounded-full flex items-center justify-center">
                <span className="font-heading font-bold text-xl text-white">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl text-brand-navy">
                  Welcome, {user?.name?.split(' ')[0]}!
                </h1>
                <p className="font-body text-sm text-brand-charcoal">
                  Manage your requests and connect with our team
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setIsEditingProfile(true)}
                className="border-brand-accent text-brand-accent hover:bg-brand-accent hover:text-white font-body text-sm rounded-full"
                data-testid="edit-profile-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-body text-sm rounded-full"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>

          {/* User Info */}
          <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2 text-brand-charcoal">
              <Mail className="w-4 h-4 text-brand-accent" />
              <span className="font-body text-sm">{user?.email}</span>
            </div>
            <div className="flex items-center gap-2 text-brand-charcoal">
              <Phone className="w-4 h-4 text-brand-accent" />
              <span className="font-body text-sm">{user?.phone}</span>
            </div>
            <div className="flex items-center gap-2 text-brand-charcoal">
              <MapPin className="w-4 h-4 text-brand-accent" />
              <span className="font-body text-sm truncate">{user?.address}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="requests" className="w-full">
          <TabsList className="bg-white rounded-xl p-1 shadow-sm mb-6 w-full md:w-auto">
            <TabsTrigger 
              value="requests" 
              className="font-body text-sm data-[state=active]:bg-brand-navy data-[state=active]:text-white rounded-lg px-6"
              data-testid="tab-requests"
            >
              <FileText className="w-4 h-4 mr-2" />
              My Requests
            </TabsTrigger>
            <TabsTrigger 
              value="messages" 
              className="font-body text-sm data-[state=active]:bg-brand-navy data-[state=active]:text-white rounded-lg px-6"
              data-testid="tab-messages"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Messages
            </TabsTrigger>
            <TabsTrigger 
              value="payments" 
              className="font-body text-sm data-[state=active]:bg-brand-navy data-[state=active]:text-white rounded-lg px-6"
              data-testid="tab-payments"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Payments
            </TabsTrigger>
          </TabsList>

          {/* My Requests Tab */}
          <TabsContent value="requests" className="mt-0">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                <h2 className="font-heading font-semibold text-lg text-brand-navy">
                  Your Quote Requests
                </h2>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Sort Order */}
                  <div className="flex items-center gap-2">
                    <ArrowUpDown className="w-4 h-4 text-brand-accent" />
                    <Select value={quoteSortOrder} onValueChange={setQuoteSortOrder}>
                      <SelectTrigger className="w-36 h-9 text-sm" data-testid="quote-sort-select">
                        <SelectValue placeholder="Sort by" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="desc">Newest First</SelectItem>
                        <SelectItem value="asc">Oldest First</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Status Filter */}
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-brand-accent" />
                    <Select value={quoteStatusFilter} onValueChange={setQuoteStatusFilter}>
                      <SelectTrigger className="w-36 h-9 text-sm" data-testid="quote-filter-select">
                        <SelectValue placeholder="Filter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="in-progress">In Progress</SelectItem>
                        <SelectItem value="complete">Complete</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={() => navigate('/#quote-form')}
                    className="bg-brand-navy hover:bg-brand-action text-white font-body text-sm rounded-full"
                    data-testid="new-quote-request-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    New Quote Request
                  </Button>
                </div>
              </div>
              
              {isLoadingQuotes ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-brand-navy" />
                </div>
              ) : quotes.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-brand-accent mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-brand-navy mb-2">No requests yet</h3>
                  <p className="font-body text-sm text-brand-charcoal mb-4">
                    {quoteStatusFilter !== 'all' ? 'No requests match your filter.' : "You haven't submitted any quote requests yet."}
                  </p>
                  <Button
                    onClick={() => navigate('/#quote-form')}
                    className="bg-brand-navy hover:bg-brand-action text-white font-body text-sm rounded-full"
                    data-testid="request-quote-btn"
                  >
                    Request a Quote
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {quotes.map((quote) => (
                    <div 
                      key={quote.id} 
                      className={`border rounded-xl p-4 transition-colors ${
                        quote.payment_status === 'paid' 
                          ? 'border-green-200 bg-green-50' 
                          : quote.payment_status === 'cash'
                            ? 'border-orange-200 bg-orange-50'
                            : quote.payment_status === 'declined'
                              ? 'border-slate-200 bg-slate-50'
                              : quote.quoted_amount 
                                ? 'border-amber-200 bg-amber-50' 
                                : 'border-slate-100 hover:border-slate-200'
                      }`}
                      data-testid={`quote-item-${quote.id}`}
                    >
                      <div className="flex flex-col gap-4">
                        {/* Header */}
                        <div className="flex flex-col md:flex-row justify-between gap-3">
                          <div className="flex-grow">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              <span className="font-heading font-semibold text-brand-navy capitalize">
                                {quote.service_type?.replace('-', ' ')} Cleaning
                              </span>
                              {getStatusBadge(quote.status)}
                              {quote.payment_status === 'paid' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Paid {quote.transaction_id && `(ID: ${quote.transaction_id.slice(-8)})`}
                                </span>
                              )}
                              {quote.payment_status === 'cash' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                  <Banknote className="w-3 h-3" />
                                  Cash Payment
                                </span>
                              )}
                              {quote.payment_status === 'declined' && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                                  <XCircle className="w-3 h-3" />
                                  Declined - Awaiting Revised Quote
                                </span>
                              )}
                            </div>
                            <p className="font-body text-sm text-brand-charcoal">
                              {quote.property_type && (
                                <span className="capitalize">{quote.property_type.replace('-', ' ')} • </span>
                              )}
                              {new Date(quote.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>

                        {/* Quote Details (if quoted) */}
                        {quote.quoted_amount && quote.payment_status !== 'declined' && (
                          <div className={`rounded-lg p-4 ${quote.payment_status === 'paid' ? 'bg-green-100' : 'bg-white border border-amber-200'}`}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                              <div>
                                <p className="font-body text-xs text-brand-charcoal">Quoted Amount</p>
                                <p className="font-heading font-bold text-2xl text-brand-navy">${quote.quoted_amount.toFixed(2)}</p>
                              </div>
                              <div>
                                <p className="font-body text-xs text-brand-charcoal">Job Address</p>
                                <p className="font-body font-medium text-brand-navy">{quote.job_address || 'N/A'}</p>
                              </div>
                              <div>
                                <p className="font-body text-xs text-brand-charcoal">Expected Completion Date</p>
                                <p className="font-body font-medium text-brand-navy">{quote.expected_completion_date || quote.completion_date || 'TBD'}</p>
                              </div>
                            </div>
                            
                            {/* Disclaimer */}
                            <div className="bg-slate-100 rounded-lg p-3 mb-4">
                              <p className="font-body text-xs text-slate-600">
                                <strong>Important:</strong> All sales are final. An inspection by the requestor at time of completion is requested. If the party is not on site, ACH Cleaning Co is not liable for any issues missed by the requesting party. Any further cleaning will incur another charge. ONLY ACH Cleaning Co administration can authorize any fee reduction or charge off.
                              </p>
                            </div>
                            
                            {/* Payment Status Indicators */}
                            {quote.payment_status === 'cash' && (
                              <div className="bg-orange-50 rounded-lg p-4 mb-4 border border-orange-200">
                                <div className="flex items-center gap-2 text-orange-700">
                                  <Banknote className="w-5 h-5" />
                                  <span className="font-body font-medium">Cash Payment Requested</span>
                                </div>
                                <p className="font-body text-xs text-orange-600 mt-1">
                                  The admin has been notified. Please coordinate with them to complete your cash payment.
                                </p>
                              </div>
                            )}
                            
                            {/* Pay and Decline Buttons */}
                            {quote.payment_status !== 'paid' && quote.payment_status !== 'cash' && (
                              <div className="flex flex-col sm:flex-row gap-3">
                                <Button
                                  onClick={() => handleQuotePayment(quote.id)}
                                  disabled={isProcessingPayment}
                                  className="flex-1 bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full"
                                  data-testid={`pay-quote-${quote.id}`}
                                >
                                  {isProcessingPayment && selectedPackage === quote.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <CreditCard className="w-4 h-4 mr-2" />
                                      Pay ${quote.quoted_amount.toFixed(2)} Now
                                    </>
                                  )}
                                </Button>
                                <Button
                                  onClick={() => handlePayCash(quote.id)}
                                  disabled={isPayingCash === quote.id}
                                  variant="outline"
                                  className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-50 font-body font-medium rounded-full"
                                  data-testid={`pay-cash-${quote.id}`}
                                >
                                  {isPayingCash === quote.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Banknote className="w-4 h-4 mr-2" />
                                      Pay in Cash
                                    </>
                                  )}
                                </Button>
                                <Button
                                  onClick={() => handleDeclineQuote(quote.id)}
                                  disabled={isDecliningQuote === quote.id}
                                  variant="outline"
                                  className="border-red-300 text-red-600 hover:bg-red-50 font-body font-medium rounded-full"
                                  data-testid={`decline-quote-${quote.id}`}
                                >
                                  {isDecliningQuote === quote.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Decline Quote
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Message if not quoted yet */}
                        {!quote.quoted_amount && quote.status === 'pending' && (
                          <p className="font-body text-sm text-brand-slate italic">
                            Awaiting quote from our team. We'll notify you when it's ready.
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Send Message */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="font-heading font-semibold text-lg text-brand-navy mb-4">
                  Contact Customer Service
                </h2>
                <form onSubmit={handleSendMessage} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="font-body text-sm text-brand-charcoal">
                      Subject
                    </Label>
                    <Input
                      id="subject"
                      value={newMessage.subject}
                      onChange={(e) => setNewMessage(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="How can we help?"
                      className="form-input-focus font-body"
                      data-testid="message-subject-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="content" className="font-body text-sm text-brand-charcoal">
                      Message
                    </Label>
                    <Textarea
                      id="content"
                      value={newMessage.content}
                      onChange={(e) => setNewMessage(prev => ({ ...prev, content: e.target.value }))}
                      placeholder="Type your message here..."
                      rows={5}
                      className="form-input-focus font-body resize-none"
                      data-testid="message-content-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSendingMessage}
                    className="w-full bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full"
                    data-testid="send-message-btn"
                  >
                    {isSendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Message History */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="font-heading font-semibold text-lg text-brand-navy mb-4">
                  Message History
                </h2>
                
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-brand-navy" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="w-12 h-12 text-brand-accent mx-auto mb-4" />
                    <h3 className="font-heading font-semibold text-brand-navy mb-2">No messages yet</h3>
                    <p className="font-body text-sm text-brand-charcoal">
                      Send us a message and we'll respond within 24 hours.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {messages.map((msg) => (
                      <div 
                        key={msg.id}
                        className={`p-4 rounded-xl ${
                          msg.is_from_customer ? 'bg-brand-surface' : 'bg-blue-50'
                        }`}
                        data-testid={`message-item-${msg.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-body font-medium text-sm text-brand-navy">
                            {msg.subject}
                          </span>
                          <span className="font-body text-xs text-brand-accent">
                            {new Date(msg.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="font-body text-sm text-brand-charcoal">
                          {msg.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="mt-0">
            <div className="space-y-6">
              {/* Service Packages - Informational */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand-navy rounded-full flex items-center justify-center">
                      <Package className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="font-heading font-semibold text-lg text-brand-navy">
                        Service Packages
                      </h2>
                      <p className="font-body text-sm text-brand-charcoal">
                        Browse our cleaning services
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate('/#quote-form')}
                    className="bg-brand-navy hover:bg-brand-action text-white font-body text-sm rounded-full"
                    data-testid="request-quote-from-packages-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Request a Quote
                  </Button>
                </div>
                
                {isLoadingPackages ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-brand-navy" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {packages.map((pkg) => (
                      <div 
                        key={pkg.id}
                        className="border border-slate-200 rounded-xl p-4"
                        data-testid={`package-${pkg.id}`}
                      >
                        <h3 className="font-heading font-semibold text-brand-navy mb-2">
                          {pkg.name}
                        </h3>
                        <p className="font-body text-sm text-brand-charcoal">
                          {pkg.description}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <p className="font-body text-sm text-blue-800">
                    <strong>How it works:</strong> Submit a quote request and our team will review your needs. 
                    Once we prepare your custom quote, you'll receive an email notification. 
                    Then you can review and pay directly from your dashboard.
                  </p>
                </div>
              </div>

              {/* Payment History */}
              <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-brand-accent rounded-full flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="font-heading font-semibold text-lg text-brand-navy">
                      Payment History
                    </h2>
                    <p className="font-body text-sm text-brand-charcoal">
                      View your past transactions
                    </p>
                  </div>
                </div>
                
                {isLoadingPayments ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-brand-navy" />
                  </div>
                ) : payments.length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 text-brand-accent mx-auto mb-4" />
                    <h3 className="font-heading font-semibold text-brand-navy mb-2">No payments yet</h3>
                    <p className="font-body text-sm text-brand-charcoal">
                      Your payment history will appear here after your first transaction.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {payments.map((payment) => (
                      <div 
                        key={payment.id}
                        className="border border-slate-100 rounded-xl p-4 hover:border-slate-200 transition-colors"
                        data-testid={`payment-${payment.id}`}
                      >
                        <div className="flex flex-col md:flex-row justify-between gap-3">
                          <div className="flex-grow">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-heading font-semibold text-brand-navy">
                                {payment.job_description || 'Cleaning Service'}
                              </span>
                              {getPaymentStatusBadge(payment.payment_status)}
                            </div>
                            <p className="font-body text-sm text-brand-charcoal">
                              {new Date(payment.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="font-heading font-bold text-xl text-brand-navy">
                              ${payment.amount.toFixed(2)}
                            </span>
                            <p className="font-body text-xs text-brand-charcoal uppercase">
                              {payment.currency}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Profile Modal */}
      {isEditingProfile && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setIsEditingProfile(false)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-navy rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <h2 className="font-heading font-bold text-lg text-brand-navy">Edit Profile</h2>
              </div>
              <button 
                onClick={() => setIsEditingProfile(false)}
                className="p-2 hover:bg-slate-100 rounded-full"
                data-testid="close-profile-modal"
              >
                <AlertCircle className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label className="font-body text-sm text-brand-charcoal">Full Name</Label>
                <Input
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="font-body"
                  placeholder="Your full name"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm text-brand-charcoal">Phone Number</Label>
                <Input
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  className="font-body"
                  placeholder="Your phone number"
                  data-testid="profile-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm text-brand-charcoal">Address</Label>
                <Textarea
                  value={profileData.address}
                  onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                  className="font-body resize-none"
                  rows={2}
                  placeholder="Your address"
                  data-testid="profile-address-input"
                />
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-body text-xs text-slate-500">
                  <Mail className="w-3 h-3 inline mr-1" />
                  Email: <span className="font-medium">{user?.email}</span>
                  <br />
                  <span className="text-xs text-slate-400">(Email cannot be changed)</span>
                </p>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setIsEditingProfile(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile}
                  className="flex-1 bg-brand-navy hover:bg-brand-action text-white rounded-full"
                  data-testid="save-profile-btn"
                >
                  {isSavingProfile ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
